import { UploadImageApi } from '@/domains/upload/apis/upload-api';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { createSharedComposable, useFileDialog } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useTracking } from '../tracking/use-tracking';
import { i18n } from '@/infrastructure/locales';

const UPLOAD_STORAGE_KEY = 'mse-tech-qa-upload-files';

/** Max file size: 4MB. Only files <= this size are accepted for upload. */
const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024;

export type StoredFileInputItem = {
  name: string;
  link: string;
  type: string;
  extension: string;
};

type FileObject = {
  file: File | null;
  id: number | string;
  name: string;
  size: string;
  isUploading: boolean;
  finishedUploading: boolean;
  imageId: string;
  type: string;
  extension: string;
  link: string;
};

function loadStoredFiles(): FileObject[] {
  try {
    const raw = sessionStorage.getItem(UPLOAD_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as StoredFileInputItem[];
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.map((item, index) => ({
      file: null,
      id: `stored-${index}-${Date.now()}`,
      name: item.name,
      size: '',
      isUploading: false,
      finishedUploading: true,
      imageId: '',
      type: item.type || 'image',
      extension: item.extension || '',
      link: item.link,
    }));
  } catch {
    return [];
  }
}

function saveStoredFiles(files: FileObject[]) {
  const toStore = files
    .filter((f) => f.finishedUploading && f.link)
    .map((f) => ({ name: f.name, link: f.link, type: f.type, extension: f.extension }));
  if (toStore.length === 0) {
    sessionStorage.removeItem(UPLOAD_STORAGE_KEY);
  } else {
    sessionStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(toStore));
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = bytes / Math.pow(k, i);

  // No decimals for KB, 1 decimal for MB+
  const decimals = i <= 1 ? 0 : 1;

  return `${size.toFixed(decimals)}${units[i]}`;
}

export const useUpload = createSharedComposable(() => {
  const { sendTracking } = useTracking();
  const uploadService = injectService<UploadImageApi>(UploadImageApi);
  const stored = loadStoredFiles();
  const selectedFiles = ref<FileObject[]>(stored);
  const id = ref(stored.length);

  const uploadError = ref<string | null>(null);
  const { files, open, reset } = useFileDialog({
    multiple: false,
    accept: 'image/*',
  });

  const upload = async (myFile: FileObject) => {
    const index = selectedFiles.value.findIndex((f) => f.id === myFile.id);
    if (index === -1) return;

    selectedFiles.value[index] = { ...selectedFiles.value[index], isUploading: true };
    try {
      if (!myFile.file) {
        return;
      }
      const res = await uploadService.uploadImage(myFile.file);
      sendTracking('mse_tech_qa_upload_img', 'agent_home');

      selectedFiles.value[index] = {
        ...selectedFiles.value[index],
        isUploading: false,
        finishedUploading: true,
        imageId: res.image_id,
        link: res.image_url,
      };
    } catch {
      // remove image if upload fails
      selectedFiles.value.splice(index, 1);
    }
  };

  watch(files, (fileList) => {
    if (!fileList?.length) return;
    uploadError.value = null;

    Array.from(fileList).forEach((f) => {
      if (f.size > MAX_FILE_SIZE_BYTES) {
        uploadError.value = `File "${f.name}" exceeds 4MB. Please choose a file ≤ 4MB.`;
        return;
      }
      const newFile = {
        file: f,
        id: id.value,
        name: f.name,
        size: formatFileSize(f.size),
        isUploading: false,
        finishedUploading: false,
        imageId: '',
        type: 'image',
        extension: f.type,
        link: '',
      };
      selectedFiles.value.push(newFile);
      upload(newFile);
      id.value++;
    });
  });

  const removeFile = (id: number | string) => {
    reset();
    const index = selectedFiles.value.findIndex((f) => f.id === id);

    if (index !== -1) {
      selectedFiles.value.splice(index, 1);
    }
  };

  const allFinishedUploading = computed(() => {
    return selectedFiles.value.every((f) => f.finishedUploading);
  });

  const fileInput = computed(() => {
    return selectedFiles.value.map((f) => {
      return {
        name: f.name,
        link: f.link,
        type: f.type,
        extension: f.extension,
      };
    });
  });

  // Persist finished uploads so they survive navigation (e.g. home -> conversation page)
  watch(selectedFiles, () => saveStoredFiles(selectedFiles.value), { deep: true });

  const openDialog = () => {
    uploadError.value = null;
    if (selectedFiles.value.length < 1) {
      open();
    }
  };

  /** Clears all uploaded files and sessionStorage so nothing is retained across navigation. */
  const clearAllUploads = () => {
    selectedFiles.value = [];
    id.value = 0;
    reset();
    sessionStorage.removeItem(UPLOAD_STORAGE_KEY);
  };

  /**
   * Fetches an image from imgUrl and uploads it (e.g. when user clicks a file example).
   * Respects the 4MB limit. Returns true on success, false on failure or oversize.
   */
  const uploadExampleImage = async (imgUrl: string): Promise<boolean> => {
    uploadError.value = null;
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      let name = 'Sample Image';
      // imgUrl.split('/').pop()?.split('?')[0] || blob.type?.replace('/', '.') || 'image.png';

      if (i18n.global.locale.value === 'cn') {
        name = '示例图片';
      } else if (i18n.global.locale.value === 'tw') {
        name = '示例圖片';
      }

      const file = new File([blob], name, { type: blob.type || 'image/png' });

      if (file.size > MAX_FILE_SIZE_BYTES) {
        uploadError.value = `Image exceeds 4MB. Please use a file ≤ 4MB.`;
        return false;
      }
      const newFile: FileObject = {
        file,
        id: id.value,
        name: name,
        size: formatFileSize(file.size),
        isUploading: false,
        finishedUploading: false,
        imageId: '',
        type: 'image',
        extension: file.type,
        link: '',
      };

      // selectedFiles.value.push(newFile);
      selectedFiles.value = [newFile];
      id.value++;
      await upload(newFile);
      return true;
    } catch {
      uploadError.value = 'Failed to load or upload image.';
      return false;
    }
  };

  return {
    openDialog,
    removeFile,
    selectedFiles,
    allFinishedUploading,
    fileInput,
    clearAllUploads,
    uploadExampleImage,
    uploadError,
    maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
  };
});
